import { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'

import { SET_LOGOUT, selectAuthState } from '@percent/cause-dashboard/context/auth'
import {
  useAuthDispatch,
  useAuthState,
  useCausesPortalAnalytics,
  useMutation
} from '@percent/cause-dashboard/common/hooks'
import { useTranslation } from 'react-i18next'
import { useServices } from '@percent/cause-dashboard/context/serviceContext/ServiceContext'
import { Alert, Button, Spacer } from '@percent/lemonade'
import { RoutePath } from '../../routes/Routes'
import { CentralCardLayoutWrapper, Loader } from '@percent/cause-dashboard/common/components'
import { logger } from '@percent/cause-dashboard/common/utility/logger'

export function AccountVerification() {
  const { authService } = useServices()
  const { search } = useLocation()
  const navigate = useNavigate()
  const { authDispatch } = useAuthDispatch()
  const { t } = useTranslation()
  const { reset: resetAnalytics } = useCausesPortalAnalytics()

  const hash = new URLSearchParams(search).get('userHash')

  const appState = useAuthState()
  const authState = selectAuthState(appState.authState)

  const [{ errorMessage }, { apiFunc: verifyAccount }] = useMutation(authService.verifyAccount, async () => {
    // Everyone signs in again after verifying, so a user who signed in before verifying is signed out
    // first; otherwise sign in would send them straight on and the verified banner would never show.
    if (authState.loggedIn) {
      try {
        await authService.logout()
      } catch (error) {
        logger(error as Error)
      }
      resetAnalytics()
      authDispatch({ type: SET_LOGOUT })
    }

    navigate(RoutePath.SIGNIN, { replace: true, state: { emailVerified: true } })
  })

  useEffect(() => {
    if (hash) {
      verifyAccount(hash)
    } else {
      navigate(RoutePath.SIGNIN)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (errorMessage) {
    return (
      <CentralCardLayoutWrapper heading={t('accountVerification.verificationFailed')}>
        <div data-testid="error-view">
          <Alert variant="error">{errorMessage}</Alert>
          <Spacer size={6} axis="vertical" />
          <Button
            stretch
            size="large"
            type="button"
            data-testid="auth-active-button"
            onClick={() => navigate(RoutePath.SIGNIN)}
          >
            {t('accountVerification.clickHereToSignIn')}
          </Button>
        </div>
      </CentralCardLayoutWrapper>
    )
  }

  return (
    <CentralCardLayoutWrapper heading={t('accountVerification.verifying')}>
      <div data-testid="verifying-view">
        <Loader />
      </div>
    </CentralCardLayoutWrapper>
  )
}
